<template>
  <div class="position-relative d-flex overflow-hidden p-0 m-0 explore viewport-height-div" style="height: 90svh">
    <div class="map-container position-relative" :class="{'sidebar-active': !isSidebarHidden}">
      <TopProgressBar :height="7" color="#70C381" ref="topProgress" />
      <ExploreV2Filters :sidebar-collapsed="isSidebarHidden" />
      <MapControls @showListAction="handleShowList" showList margin-top="4rem">
        <!-- The selected hike's warnings (the card's hike): the round button
             beside the location control, as on the hike map. -->
        <template #beside-location><SelectedHikeRouteAlerts /></template>
      </MapControls>
      <!-- The pill row with the "On this map" control (the Hazards count among them), as on /map. -->
      <OnThisMapButtons :button-clicked="mapButtonClicked" :buttons="buttons" :maps-button-id="mapsButtonId(buttons)" style="margin-top: 3rem" />

      <div id='map' ref="map" class="mapbox m-0 p-0"></div>
      <TrailDataCard @card-active="handleTrailCardActive" @closed="deselectTrail" />
      <div class="bottom-controls-container">
        <div v-if="miniCoordsBar" class="coordsbar">
          <span class="material-icons">my_location</span> {{ miniCoordsBar }}
        </div>
        <ScaleBar v-if="mapInstance" :map="mapInstance" :latitude="currentLatitude" />
      </div>
    </div>

    <ExploreV2SideBar
      :collapsed="isSidebarHidden"
      @update:collapsed="setSidebarHidden"
    />

    <button
      class="btn back-to-map justify-content-center"
      :class="{'show': !isSidebarHidden, 'hide': isSidebarHidden}"
      @click="hideRightSidebar">
      <div class="d-flex align-items-center">
        <span class="material-icons mr-2">map</span> {{ $t('explore_v2.explore.back_to_map') }}
      </div>
    </button>
    <div style="display: none" class="my-cool-class"></div>
    <BaseMapStylesDialog />
    <MapDataPOIDetailDialog/>
    <GoToLocationDialog />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { mapGetters, mapState } from 'vuex';
import ExploreV2Filters from "./components/ExploreV2Filters.vue";
import ExploreV2SideBar from "./components/ExploreV2SideBar.vue";
import BaseMapStylesDialog from '.././base_map_dialog/BaseMapStylesDialog.vue';
import OnThisMapButtons from '../map/components/on_this_map/OnThisMapButtons.vue';
import { mapsButtonId } from '../../utils/onThisMap';
import MapControls from '../components/MapControls.vue';
import SelectedHikeRouteAlerts from '../route_alerts/SelectedHikeRouteAlerts.vue';
import ScaleBar from '../components/ScaleBar.vue';
import TrailDataCard from "../trail_data_card/TrailDataCard.vue";
import {HiikerMapStyle, SavedCenter, ThreeDimensionalMapManager} from '../../utils/models/MapStyles';
import {resolveExploreSavedCenter} from '../../utils/helpers/exploreSavedCenter';
import { MapDataCreditControl } from '../../utils/helpers/MapDataCreditControl';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import { TypesenseSearchable } from '../../utils/models/TypesenseSearchable';
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import { Modal } from 'bootstrap'
import TopProgressBar from '../../utils/shared_components/TopProgressBar.vue';
import mapboxgl from "mapbox-gl";
import store from "./store/store";
import createGeoLocate from "../../utils/helpers/GeoLocateHelper";
import getQueryParams from "../../utils/helpers/GetQueryParamsHelper";
import { summitPeakIdFromQuery } from "./peakSummitCarousel";
import emitter from "../../utils/bus";
import {LayerRegistry} from "../../utils/layer-registry";
import {GridRefUtils} from "../../utils/helpers/GridRefPreference";
import {IndexedDBCache} from "../../utils/crosshair_altitude_service/indexed_db_cache";
import {getElevation} from "../../utils/crosshair_altitude_service/pixel_methods";
import { throttle } from 'lodash-es';
import MapDataPOIDetailDialog from "../map_data_poi_dialog/MapDataPOIDetailDialog.vue";
import GoToLocationDialog from "../go_to_location_dialog/GoToLocationDialog.vue";

export default defineComponent({
  components: {
    SelectedHikeRouteAlerts,
    MapDataPOIDetailDialog,
    ExploreV2Filters,
    ExploreV2SideBar,
    BaseMapStylesDialog,
    OnThisMapButtons,
    MapControls,
    ScaleBar,
    TrailDataCard,
    TopProgressBar, // *** REGISTER THE NEW COMPONENT ***
    GoToLocationDialog,
  },
  beforeCreate() {
    this.$store.dispatch('exploreV2/initialiseStore');
    this.$store.dispatch('mapStyles/initialiseStore');
  },
  async mounted() {
    mapboxgl.accessToken = "pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w";
    const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();

    window.addEventListener('explore:hideSidebar', this.handleMobileSidebarHide);

    // Handle URL parameters for initial map position
    const queryParams = getQueryParams(window.location.href);
    const sourceParam = typeof queryParams.source === 'string' ? queryParams.source.toLowerCase() : null;
    if (sourceParam === 'community') {
      store.commit('exploreV2/setSearchSource', 'community');
    } else if (sourceParam === 'official' || sourceParam === 'hikes') {
      store.commit('exploreV2/setSearchSource', 'hikes');
    }

    if (queryParams.lng !== undefined && queryParams.lat !== undefined) {
      const lng = parseFloat(queryParams.lng);
      const lat = parseFloat(queryParams.lat);
      if (!isNaN(lng) && !isNaN(lat)) {
        store.commit('exploreV2/setCenter', [lng, lat]);
      }
    }
    if (queryParams.zoom !== undefined) {
      const zoom = parseFloat(queryParams.zoom);
      if (!isNaN(zoom)) {
        store.commit('exploreV2/setZoom', zoom);
      }
    }

    // A peak's "View all on map" (/explore?summit_peak_id=…): its hikes open in the pager once
    // the map's first style has loaded and placed the camera.
    let pendingSummitPeakId = summitPeakIdFromQuery(queryParams);

    const savedCenter = resolveExploreSavedCenter(localStorage.getItem('currentCenter'), localStorage.getItem('exploreCenter'));
    const hasSavedCenter = savedCenter !== null;
    if (!queryParams.lng && !queryParams.lat && savedCenter) {
      store.commit('exploreV2/setCenter', [savedCenter.lng, savedCenter.lat]);
      store.commit('exploreV2/setZoom', savedCenter.zoom);
    }

    const map = new mapboxgl.Map({
      container: 'map', // Ensure an element with id="map" exists within your Explore component or template
      // Access store state directly via imported store
      center: store.state.exploreV2.center,
      zoom: store.state.exploreV2.zoom ?? 0,
      attributionControl: false,
      maxPitch: 85,
      style: HiikerMapStyle.getCurrentStyle(isProOrProPlus).styleUrl,
    });

    // Explore runs without GL JS's AttributionControl, so a style whose data needs crediting —
    // Lidar's Mapterhorn terrain — has nothing else on screen saying so.
    map.addControl(new MapDataCreditControl(), 'bottom-right');

    window.map = map; // Assign map instance to window global
    window.layerRegistry = new LayerRegistry(map); // Assuming LayerRegistry is defined elsewhere
    const canvas = map.getCanvasContainer();
    canvas.style.cursor = "crosshair";

    this.mapInstance = map;
    this.currentLatitude = map.getCenter().lat;

    const gridRef = GridRefUtils.preference();
    const cache = new IndexedDBCache();
    const handleMouseMove = async (e: mapboxgl.MapMouseEvent & Object) => {
      try {
        const coords = gridRef.convert(e.lngLat);
        const elevation = await getElevation(e.lngLat, e.target.getZoom(), cache);
        this.miniCoordsBar = `${coords.toString()} ${elevation}`;
      } catch (error) {
        // swallow errors to avoid blocking updates
      }
    };
    const throttledMouseMove = throttle(handleMouseMove, 100);
    this.mouseMoveHandler = throttledMouseMove;
    map.on('mousemove', throttledMouseMove);

    map.on('move', () => {
      this.currentLatitude = map.getCenter().lat;
    });

    // Call the method defined below
    this.mapInitialised();

    // Cluster navigation window functions
    (window as any).clusterNavigate = (direction: 'next' | 'prev') => {
      store.dispatch('exploreV2/navigateCluster', direction);
    };
    (window as any).clearClusterNav = () => {
      store.commit('exploreV2/clearClusterNav');
      store.dispatch('exploreV2/removeClusterPolylinesFromMap');
    };
    // The peak sheet's "View all on map", opened from this page's own POI sheet.
    (window as any).exploreShowPeakSummitHikes = (peakId: string) => {
      store.dispatch('exploreV2/openPeakSummitCarousel', peakId);
    };

    // --- Mapbox Event Listeners ---
    map.on('dragend', (e) => {
      // Use imported store directly for commits/dispatches
      store.commit('exploreV2/setCenterAndZoom', { center: map.getCenter().toArray(), zoom: map.getZoom() });
    });

    map.on('zoomend', (e) => {
      store.commit('exploreV2/setCenterAndZoom', { center: map.getCenter().toArray(), zoom: map.getZoom() });
    });

    // Shared with the main map and the planner, so each opens where the user last looked. Saved on
    // moveend, as the main map does: dragend fires before a drag's inertia has finished moving the map.
    map.on('moveend', () => SavedCenter.saveCurrentCenter());

    var initalBoundsSet = false; // Local variable scope is fine

    map.on('load', () => {
      emitter.emit('map-loaded');
      createGeoLocate(); // Call helper function
    });

    map.on('style.load', async () => {
      emitter.emit('map-style-loaded');
      // Access window globals and store state/dispatch directly
      // Only get location from IP if no URL parameters are set
      const hasUrlParams = queryParams.lng && queryParams.lat;
      const hasRegionOrCountry = window.region_id != null || window.country_id != null;
      const shouldGetLocationFromIp = !initalBoundsSet && !hasUrlParams && !hasSavedCenter && !hasRegionOrCountry && store.state.country == null && store.state.region == null;

      // Debug logging to help diagnose the "long distance trails" bug
      console.log('[Explore] style.load fired:', {
        initalBoundsSet,
        hasUrlParams,
        hasRegionOrCountry,
        shouldGetLocationFromIp,
        storeCenter: store.state.exploreV2.center,
        storeZoom: store.state.exploreV2.zoom,
        mapZoom: map.getZoom(),
        mapCenter: map.getCenter().toArray().map(n => n.toFixed(2))
      });

      if (shouldGetLocationFromIp) {
        // Wait for flyTo animation to complete before querying Typesense
        // This prevents the bug where world-spanning bounds are used for the initial query
        console.log('[Explore] Getting location from IP...');
        await store.dispatch('exploreV2/getLocationFromIp');
        console.log('[Explore] Location from IP complete, map now at:', {
          zoom: map.getZoom(),
          center: map.getCenter().toArray().map(n => n.toFixed(2))
        });
      }
      initalBoundsSet = true;

      // Handle region/country from window - these will trigger their own Typesense queries
      // via store subscriptions when the map position updates
      if (hasRegionOrCountry) {
        console.log('[Explore] Loading region/country from window, skipping initial Typesense query');
        store.dispatch('exploreV2/setRegionIfOnWindow');
        store.dispatch('exploreV2/setCountryIfOnWindow');
        // Don't call getTypesenseResponse here - it will be triggered by the store
        // subscription when setCenterAndZoom is called after the map moves to the region/country
        store.dispatch('exploreV2/setClusterOnMapFromTypesenseResponse');
        store.dispatch('exploreV2/setTrailOnMap', true);
      } else {
        store.dispatch('exploreV2/getTypesenseResponse');
        store.dispatch('exploreV2/setClusterOnMapFromTypesenseResponse');
        store.dispatch('exploreV2/setTrailOnMap', true);
      }

      // Once: style.load fires again on every map style switch.
      if (pendingSummitPeakId) {
        const peakId = pendingSummitPeakId;
        pendingSummitPeakId = null;
        store.dispatch('exploreV2/openPeakSummitCarousel', peakId);
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener('explore:hideSidebar', this.handleMobileSidebarHide);
    (window as any).exploreShowPeakSummitHikes = null;
    if (this.mapInstance && this.mouseMoveHandler) {
      this.mapInstance.off('mousemove', this.mouseMoveHandler);
    }
    this.mouseMoveHandler = null;
    this.mapInstance = null;
    this.miniCoordsBar = null;
  },
  data() {
    return {
      shouldRefreshChart: false,
      isSidebarHidden: false,
      isMobile: false,
      buttons: [
        { id: 0, name: 'Maps', icon: 'layers', enabled: false, dropIcon: true },
        { id: 1, name: '', icon: 'search', enabled: false, dropIcon: true }
      ],
      mapInstance: null as mapboxgl.Map | null,
      currentLatitude: 0,
      miniCoordsBar: null as string | null,
      mouseMoveHandler: null as ReturnType<typeof throttle> | null,
    };
  },
  computed: {
    // Cast the ref type for TypeScript within computed or methods if needed
    typedTopProgress(): typeof TopProgressBar | null {
      return this.$refs.topProgress as typeof TopProgressBar || null;
    },
    typesenseResponse() {
      // @ts-ignore
      return this.$store.state.exploreV2.typesenseResponse;
    },
    inputValue: {
      get() {
        // @ts-ignore
        return this.$store.state.exploreV2.searchString;
      },
      set(val) {
        // @ts-ignore
        this.$store.commit('exploreV2/setSearchString', val);
      },
    },
    triggerLoading() {
      // @ts-ignore
      return this.$store.state.exploreV2.triggerLoading;
    },
    ...mapState({
      // @ts-ignore
      trail: state => state.exploreV2.trail,
      // @ts-ignore
      recording: state => state.exploreV2.recording,
      // @ts-ignore - Added for _3dEnabled if it comes from Vuex state
      _3dEnabled: state => state.someModule?.is3DEnabled, // Example path
    }),
  },
  watch: {
    _3dEnabled(newVal) { // Removed oldVal as it's not used
      // Ensure buttons array exists and has an element at index 1
      if (this.buttons && this.buttons.length > 1) {
        this.buttons[1].enabled = newVal;
      }
    },
    triggerLoading() {
      // Use the typed computed property or cast directly
      const progressRef = this.typedTopProgress;
      // const progressRef = this.$refs.topProgress as any; // Alternative less safe cast

      if (progressRef) {
        progressRef.start();
        // Simulate loading ends
        setTimeout(() => {
          progressRef.finish();
        }, 2000); // Your existing timeout
      } else {
        console.warn('topProgress ref not available yet.');
      }

    },
    trail: {
      handler(newTrail) {
        if (newTrail) {
          if (typeof window.trailCardDataHike === 'function') {
            window.trailCardDataHike(newTrail);
          }
          // Pass cluster nav state to the card if active
          const clusterState = store.state.exploreV2;
          if (clusterState.isClusterNavActive && typeof (window as any).trailCardSetClusterNav === 'function') {
            (window as any).trailCardSetClusterNav(true, clusterState.clusterActiveIndex, clusterState.clusterLeaves.length, clusterState.clusterOrigin);
          }
          if (this.isMobile) {
            this.isSidebarHidden = true;
            this.toggleSidebarClasses();
          }
        } else if (!this.recording) {
          if (typeof window.trailCardClearData === 'function') {
            window.trailCardClearData();
          }
        }
      },
      deep: true,
    },
    recording: {
      handler(newRecording) {
        if (newRecording) {
          if (typeof window.trailCardDataRecording === 'function') {
            // Normalize lineString coordinates to strip distance values
            // Backend returns [lon, lat, altitude, distance] or [lon, lat, distance]
            // Chart expects [lon, lat, altitude] or [lon, lat]
            const normalized = { ...newRecording }
            if (normalized.lineString) {
              const parsed = typeof normalized.lineString === 'string' ? JSON.parse(normalized.lineString) : { ...normalized.lineString }
              const hasElevation = (normalized.elevationGainMeters ?? 0) > 0
              const coords = parsed.features?.[0]?.geometry?.coordinates
              if (coords) {
                parsed.features[0].geometry.coordinates = coords.map((c: number[]) => {
                  if (hasElevation && c.length >= 3) {
                    return [c[0], c[1], c[2]]
                  }
                  return [c[0], c[1]]
                })
              }
              normalized.lineString = parsed
            }
            window.trailCardDataRecording(normalized);
          }
          // Pass cluster nav state to the card if active
          const clusterState = store.state.exploreV2;
          if (clusterState.isClusterNavActive && typeof (window as any).trailCardSetClusterNav === 'function') {
            (window as any).trailCardSetClusterNav(true, clusterState.clusterActiveIndex, clusterState.clusterLeaves.length, clusterState.clusterOrigin);
          }
          if (this.isMobile) {
            this.isSidebarHidden = true;
            this.toggleSidebarClasses();
          }
        } else if (!this.trail) {
          if (typeof window.trailCardClearData === 'function') {
            window.trailCardClearData();
          }
        }
      },
      deep: true,
    },
  },
  methods: {
    mapsButtonId,
    async mapInitialised() {
      // Access store directly or via this.$store
      await store.dispatch('exploreV2/mapInitialised');
      // Alternatively: await this.$store.dispatch('exploreV2/mapInitialised');
    },
    hideRightSidebar() {
      this.setSidebarHidden(!this.isSidebarHidden);
    },
    setSidebarHidden(value: boolean) {
      if (this.isSidebarHidden === value) {
        return;
      }
      this.isSidebarHidden = value;
      this.toggleSidebarClasses();
    },
    handleTrailCardActive(isActive: boolean) {
      if (!this.isMobile) {
        this.setSidebarHidden(isActive);
      }
    },
    /**
     * The card's close: the trail goes with it, its route off the map, as a
     * click on empty map does. (Its warnings button follows the card.)
     */
    deselectTrail() {
      store.commit('exploreV2/clearClusterNav');
      store.dispatch('exploreV2/removeClusterPolylinesFromMap');
      store.commit('exploreV2/setTrailEncodedPolyline', null);
      store.commit('exploreV2/setTrail', null);
      store.commit('exploreV2/setRecording', null);
    },
    handleShowList() {
      this.setSidebarHidden(false);
    },
    toggleSidebarClasses() {
      setTimeout(() => {
        if (window.map && typeof window.map.resize === 'function') {
          window.map.resize();
        }
        this.shouldRefreshChart = !this.shouldRefreshChart; // Toggling a boolean is simpler
      }, 350); // Consider making timing a constant or variable
    },
    handleMobileSidebarHide() {
      if (this.isSidebarHidden) return;
      this.setSidebarHidden(true);
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 1000;
      // Only hide sidebar if turning mobile and it's not already hidden
      if (this.isMobile && !this.isSidebarHidden) {
        this.setSidebarHidden(true);
      }
      // Optional: handle resizing back to desktop if needed
      else if (!this.isMobile && this.isSidebarHidden) {
        // Decide if you want to automatically show sidebar on desktop resize
        // this.isSidebarHidden = false;
        // this.toggleSidebarClasses();
      }
    },
    async search(input: string) {
      if (input.length < 1) return [];
      // Assuming TypesenseSearchable.get returns a promise with { hits: [...] }
      const results = await TypesenseSearchable.get(input);
      return results?.hits ?? []; // Return empty array if hits is null/undefined
    },
    getResultValue(result: any) {
      // Use optional chaining for safer access
      return result?.document?.name ?? '';
    },
    async mapButtonClicked(id: number) {
      switch (id) {
        case 0: // Map Layers
          if (window.user) {
            const mapStylesDialog = document.getElementById("map-styles-dialog");
            const modal = new Modal(mapStylesDialog);
            modal.show();
          } else {
            // @ts-ignore
            window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
          }
          break;
        case 1: {
          const goToDialog = document.getElementById('go-to-location-dialog');
          if (goToDialog) {
            const modal = Modal.getOrCreateInstance(goToDialog);
            modal.show();
          }
          break;
        }
      }
    },
  },
  created() {
    this.checkMobile(); // Initial check
    window.addEventListener('resize', this.checkMobile);

    // Ensure jQuery is available before using $
    if (typeof $ !== 'undefined') {
      $(() => {
        // @ts-ignore - Assuming $ is jQuery
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new Tooltip(tooltipTriggerEl))
      });
    }
  },
});
</script>


<style lang="scss">
/* Your existing styles */
.explore {

  .map-buttons-container{
    top: 6rem !important;
  }

  .map-container { flex: 1; position: relative; }

  /* Position adjustment when sidebar is active */
  /* Specific positioning (keep as is if it works) */
  .button-container { top: 11.5vh; }

  .my-cool-class { background-color: #202020; }

  @media (max-width: 900px) {
    .mapboxgl-control-container {
      position: absolute;
      top: 60px; /* Keep this adjustment */
    }
    .mapboxgl-ctrl-logo { display: none !important; }
  }

  .wiki-result {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1em;
    cursor: pointer; /* Indicate clickable */
  }
  .wiki-result:hover {
    background-color: rgba(255, 255, 255, 0.05); /* Slight hover effect */
  }

  .map-container {
    flex: 1 1 auto;
    max-width: 100%;
    position: relative; /* Already set */
    z-index: 8; /* Base map container z-index */
  }

  .map-content { /* Not used in template? */
    width: 100%;
    height: 100%;
    position: relative;
  }

  .mapbox {
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: 2; /* Below controls */
  }

  /* Not used in template? */
  .left-sidebar-btn {
    position: absolute;
    top: 44%;
    left: -3px;
    padding: 20px 2px; /* Adjusted padding */
    background: #202020;
    color: #fff;
    border: none;
    z-index: 100;
    border-radius: 0 5px 5px 0; /* Correct rounding */
  }

  /* Not used in template? */
  #map-tools-container { bottom: 106px; }

  /* Not used in template? */
  .chart-div {
    bottom: 0;
    width: 100%;
    height: auto;
    position: absolute;
    z-index: 100;
    background-color: rgba(0, 0, 0, 0.74); /* Use rgba */
  }

  /* Not used in template? */
  .open-list-mobile { display: none; }

  /* Not used in template? */
  .mapboxgl-ctrl-scale {
    border-radius: 0 0 10px 10px;
    position: absolute;
    top: 59px;
    left: 5px;
    padding: 5px;
    background-color: rgba(255, 255, 255, 0.6); /* Use rgba */
    border: 1px solid #ccc;
    font-size: 12px;
    line-height: 1.5;
    z-index: 1000;
  }

  .viewport-height-div {
    position: relative; /* Already set */
    height: 90vh; /* Set via inline style */
    /* Ensure it contains positioned children correctly */
    overflow: hidden; /* Already set */
  }

  .back-to-map {
    display: none; /* Hidden by default */
    opacity: 0; /* Start transparent */
    background: #2c2c2c;
    border-color: #2c2c2c;
    color: #fff;
    padding: 0.8em 1em !important; /* Adjusted padding */
    box-shadow: 0px 15px 42px 6px rgba(0,0,0,0.49);
    position: absolute;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 40px); /* Add fallback for env() */
    left: 50%; /* Center using transform */
    transform: translateX(-50%); /* Center using transform */
    z-index: 11; /* Above map container content */
    width: auto; /* Let content determine width */
    max-width: 80%; /* Limit max width */
    margin: 0; /* Remove margin auto */
    border-radius: 5px;
    transition: opacity 0.3s ease, display 0s linear 0.3s; /* Delay display none */
    white-space: nowrap; /* Prevent wrapping */
  }

  .back-to-map.show {
    display: flex;
    opacity: 1;
    transition: opacity 0.3s ease;
  }

  /* This rule might be redundant if .show handles display */
  /* .back-to-map.hide {
    display: none;
    opacity: 0;
  } */

  @media (max-width: 600px) {
    /* Back to map button visibility already handled by .show */
    h6 { margin-bottom: 1em !important; }
  }

  /* Hide back-to-map on desktop */
  @media (min-width: 1001px) { /* Adjusted breakpoint */
    .back-to-map { display: none !important; }
  }

  @media (max-width: 420px) {
    /* Further mobile adjustments */
    .button-container { top: 6vh; } /* Keep if used */
  }
}
</style>
